import ControlMode from '.'
import {Vector2} from 'three'

// This mode handles controlling the rotation with the mouse as well
// as the zoom with the mousewheel. Since these controls should take
// priority, the methods `onUse` and `onRelease` are called on instances
// of this class when a user takes control.
class MouseMode extends ControlMode {
  // Control mode initialization
  constructor(...args) {
    super(...args)
    this.init = this.init.bind(this)
    this.hold = this.hold.bind(this)
    this.release = this.release.bind(this)
    this.keyChange = this.keyChange.bind(this)
    this.mouseDown = this.mouseDown.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.bindHandlers = this.bindHandlers.bind(this)
    this.mouseMove = this.mouseMove.bind(this)
    this.mouseUp = this.mouseUp.bind(this)
    this.mouseTranslate = this.mouseTranslate.bind(this)
    this.mouseOrbit = this.mouseOrbit.bind(this)
  }

  init() {
    this.translating = false
    this.shift = false

    this.drag = {
      sensitivity: 0.4,
      start: new Vector2(),
      end: new Vector2()
    }

    // ES6 hack to bind methods - rebind the functions to bind to this instance
    // see https://twitter.com/getify/status/1102606263656083456 for more information
    this.onScroll = this.onScroll.bind(this)
    this.mouseDown = this.mouseDown.bind(this)
    this.keyChange = this.keyChange.bind(this)
    this.mouseMove = this.mouseMove.bind(this)
    this.mouseUp = this.mouseUp.bind(this)

    this.controls.$().on('mousewheel DOMMouseScroll', this.onScroll)
    this.controls.$().on('mousedown', this.mouseDown)
    this.controls.$().on('contextmenu', event => event.preventDefault())
    $(window).on('keydown keyup', this.keyChange)
  }

  // Used to externally control the control focus
  hold() {
    return this.onUse()
  }
  release() {
    return this.onRelease()
  }

  keyChange(e) {
    if (e.keyCode === 16) {
      if (e.type === 'keydown') {
        this.shift = this.translating = true
      }
      if (e.type === 'keyup') {
        this.shift = this.translating = false
      }
    }
  }

  mouseDown(event) {
    if (!this.active) {
      if (typeof this.onUse === 'function') {
        this.onUse()
      }
    }
    event.preventDefault()
    this.bindHandlers(true)
    this.drag.start.set(event.clientX, event.clientY)

    // if we right clicked, lets translate
    if (event.which === 3) {
      this.translating = true
    }
  }

  onScroll(e) {
    if (!this.active) {
      if (typeof this.onUse === 'function') {
        this.onUse()
      }
    }
    e.stopPropagation()
    e.preventDefault()

    // As in: https://github.com/tbuser/thingiview.js/blob/master/javascripts/thingiview.js#L193
    const original = e.originalEvent
    const delta = original.wheelDelta || -40 * original.detail
    if (delta > 0) {
      this.controls.changeZoom(+0.1)
    } else {
      this.controls.changeZoom(-0.1)
    }
    if (this.active) {
      this.onRelease()
    }
  }

  // Secondary handlers
  bindHandlers(engage) {
    if (engage == null) {
      engage = true
    }
    const action = engage === true ? 'on' : 'off'
    this.controls.$()[action]('mousemove', this.mouseMove)
    this.controls.$()[action]('mouseup', this.mouseUp)
  }

  mouseMove(event) {
    if (this.translating) {
      this.mouseTranslate(event)
    }
    this.mouseOrbit(event)
  }

  mouseUp() {
    if (this.active) {
      if (typeof this.onRelease === 'function') {
        this.onRelease()
      }
    }
    this.bindHandlers(false)
    // If we let go of the mouse and shift isn't held down
    // then translation time is over
    if (this.translating && !this.shift) {
      this.translating = false
    }
  }

  // Movement helers
  mouseTranslate(event) {
    const drag = this.drag.end
      .set(event.clientX, event.clientY)
      .clone()
      .sub(this.drag.start)
      .multiplyScalar(this.drag.sensitivity)
    this.drag.start.copy(this.drag.end)

    this.controls.translateUp(drag.y)
    this.controls.translateRight(drag.x)
  }

  mouseOrbit(event) {
    const drag = this.drag.end
      .set(event.clientX, event.clientY)
      .clone()
      .sub(this.drag.start)
      .multiplyScalar(this.drag.sensitivity)
    this.drag.start.copy(this.drag.end)

    this.controls.orbitUp(this.controls.orbitIncrement * drag.y)
    this.controls.orbitRight(this.controls.orbitIncrement * -drag.x)
  }
}

export default MouseMode
